import { useRef, useMemo, useEffect, useCallback, useState } from 'react';
import styles from './FJCreationEditPanel.module.less';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { MOUSE_EVENT_TYPE } from '../js/FJViewCanvas';
import FJEditFloatToolContainer from '../container/FJEditFloatToolContainer';
import { ZOOM_MIN, ZOOM_MAX } from '../../../../config/dataConfig/FJImageManipulationData';
import FJCreationEditCompareContainer from '../container/FJCreationEditCompareContainer';
import FJCreationEditStatusBoxContainer from '../container/FJCreationEditStatusBoxContainer';

const FJCreationEditPanel = props => {
    const {
        curBrushType,
        brushSize = 10,
        zoom = 1,
        isActive,

        isMouseMove,
        isShowBrushInCenter,
        isShowEditCanvas,
        isShowFloatTool,
        isSlideCompare,
        initImagePadding,
        offsetTop,
        editImageLoadedStatus,

        initViewCanvas,
        mouseDownHandler,
        mouseUpHandler,
        mouseMoveHandler,
        mouseEnterHandler,
        mouseLeaveHandler,
        setZoom,

        bgColor,
    } = props;

    const [mouseInPanel, setMouseInPanel] = useState(false);
    const [mouseInToolPanel, setMouseInToolPanel] = useState(false);
    const [mousePoint, setMousePoint] = useState({ x: 0, y: 0 });

    const canvasRef = useRef(null);

    useEffect(() => {
        const viewSize = {
            width: canvasRef.current.clientWidth,
            height: canvasRef.current.clientHeight,
        };

        canvasRef.current && initViewCanvas && initViewCanvas(canvasRef.current, viewSize);
    }, [initViewCanvas]);

    const handleRefresh = useCallback(() => {
        const viewSize = {
            width: canvasRef.current.clientWidth,
            height: canvasRef.current.clientHeight,
        };

        canvasRef.current && initViewCanvas && initViewCanvas(canvasRef.current, viewSize);
    }, [initViewCanvas]);

    const handleWheel = useCallback(
        event => {
            if (!mouseInPanel) return;

            if (event.deltaY < 0) {
                setZoom(Math.min(zoom + 0.1, ZOOM_MAX), event);
            } else {
                setZoom(Math.max(zoom - 0.1, ZOOM_MIN), event);
            }
        },
        [setZoom, zoom, mouseInPanel],
    );

    useEffect(() => {
        if (!isShowEditCanvas) return;
        window.addEventListener('wheel', handleWheel);
        return () => {
            window.removeEventListener('wheel', handleWheel);
        };
    }, [handleWheel, isShowEditCanvas]);

    const mouseMove = useCallback(
        event => {
            if (!isShowEditCanvas) return;

            const canvasRefRect = canvasRef.current.getBoundingClientRect();
            setMousePoint({ x: event.clientX - canvasRefRect.left, y: event.clientY - canvasRefRect.top });
            mouseMoveHandler && mouseMoveHandler(event, mouseInToolPanel);
        },
        [mouseMoveHandler, isShowEditCanvas, mouseInToolPanel],
    );

    const _handleMouseUp = useCallback(
        event => {
            if (!isShowEditCanvas) return;
            mouseUpHandler && mouseUpHandler(event, mouseInToolPanel);
            window.removeEventListener('mouseup', _handleMouseUp);
        },
        [mouseUpHandler, isShowEditCanvas, mouseInToolPanel],
    );

    const _handleMouseDown = useCallback(
        event => {
            if (!isShowEditCanvas) return;
            mouseDownHandler && mouseDownHandler(event, mouseInToolPanel);

            window.addEventListener('mouseup', _handleMouseUp);
        },
        [mouseDownHandler, _handleMouseUp, isShowEditCanvas, mouseInToolPanel],
    );

    const _handleMouseEnter = useCallback(() => {
        setMouseInPanel(true);
        if (!isShowEditCanvas) return;

        mouseEnterHandler && mouseEnterHandler();
    }, [isShowEditCanvas, mouseEnterHandler]);

    const _handleMouseLeave = useCallback(() => {
        setMouseInPanel(false);
        if (!isShowEditCanvas) return;
        mouseLeaveHandler && mouseLeaveHandler();
    }, [isShowEditCanvas, mouseLeaveHandler]);

    const mouseStyleClass = useMemo(() => {
        return classNames({
            [styles.mouse_move]: isMouseMove,
            [styles.mouse_erase]: !isMouseMove && curBrushType === MOUSE_EVENT_TYPE.MASK_ERASE,
            [styles.mouse_draw]: !isMouseMove && curBrushType === MOUSE_EVENT_TYPE.MASK_DRAW,
            [styles.mouse_slide_compare]: isSlideCompare,
        });
    }, [curBrushType, isMouseMove, isSlideCompare]);

    //#region UI 渲染方法
    const renderMouse = useMemo(() => {
        return (
            <div
                className={styles.mouse_point}
                style={{
                    top: !isShowBrushInCenter ? mousePoint.y : '48%',
                    left: !isShowBrushInCenter ? mousePoint.x : '50%',
                    width: brushSize * zoom,
                    height: brushSize * zoom,
                    display:
                        (!mouseInPanel || mouseInToolPanel || !isShowEditCanvas || isSlideCompare) &&
                        !isShowBrushInCenter
                            ? 'none'
                            : '',
                }}
            ></div>
        );
    }, [
        isShowBrushInCenter,
        mousePoint,
        brushSize,
        mouseInPanel,
        mouseInToolPanel,
        isShowEditCanvas,
        isSlideCompare,
        zoom,
    ]);
    //#endregion

    return (
        <div
            className={classNames(styles.container, mouseStyleClass, {
                [styles.not_show_canvas]: !isShowEditCanvas,
                [styles.active]: isActive,
            })}
            style={{ backgroundColor: bgColor }}
            onMouseDown={_handleMouseDown}
            onMouseMove={mouseMove}
            onMouseEnter={_handleMouseEnter}
            onMouseLeave={_handleMouseLeave}
        >
            <canvas
                className={classNames(styles.canvas, { [styles.not_show_canvas]: !isShowEditCanvas })}
                ref={canvasRef}
            ></canvas>

            <FJCreationEditStatusBoxContainer
                isShow={!isShowEditCanvas && isActive}
                initImagePadding={initImagePadding}
                offsetTop={offsetTop}
                editImageLoadedStatus={editImageLoadedStatus}
                onRefresh={handleRefresh}
            />

            {/* 鼠标样式 画笔样式 */}
            {renderMouse}

            <FJCreationEditCompareContainer />

            {isShowFloatTool && (
                <FJEditFloatToolContainer
                    isShowEditCanvas={isShowEditCanvas}
                    setMouseInToolPanel={setMouseInToolPanel}
                />
            )}
        </div>
    );
};

FJCreationEditPanel.propTypes = {
    initViewCanvas: PropTypes.func,
    mouseDownHandler: PropTypes.func,
    mouseUpHandler: PropTypes.func,
    mouseMoveHandler: PropTypes.func,
    curBrushType: PropTypes.string,
    isMouseMove: PropTypes.bool,
    brushSize: PropTypes.number,
    zoom: PropTypes.number,
    isShowBrushInCenter: PropTypes.bool,
    isShowEditCanvas: PropTypes.bool,
    isShowFloatTool: PropTypes.bool,
    isActive: PropTypes.bool,
    setZoom: PropTypes.func,
    isSlideCompare: PropTypes.bool,
    bgColor: PropTypes.string,
    mouseEnterHandler: PropTypes.func,
    mouseLeaveHandler: PropTypes.func,
    initImagePadding: PropTypes.object,
    offsetTop: PropTypes.number,
    editImageLoadedStatus: PropTypes.string,
};

export default FJCreationEditPanel;
